<template>
  <div>
    <vue-loading :active="isLoading" :is-full-page="false" :enforce-focus="false" />

    <div class="d-md-none">
      <h2 class="normalText sr-only">{{ $t(tKey('label.table')) }}</h2>
      <CardList
        :items="recentOrders"
        :list-aria-label="$t(tKey('label.table'))"
        :empty-text="$t('system.label.noData')"
        :item-aria-label="mobileItemAriaLabel"
        :item-key="mobileItemKey"
        @item-click="openOrderDetails"
      >
        <template #item="{ item, ariaLabel, onClick }">
          <CardListItem
            :aria-label="ariaLabel"
            @click="onClick()"
          >
            <BlockContainer :rows="mobileFields(item)">
              <template #top>
                <StatusMessage
                  v-if="showIqsrOrderNumStatus(item)"
                  class="order-overview__pickup-status"
                  variant="info"
                >
                  {{ pickupStatusText(item) }}
                </StatusMessage>
              </template>
            </BlockContainer>
          </CardListItem>
        </template>
      </CardList>

      <div class="order-overview__mobile-actions" role="region" :aria-label="$t(tKey('subTitle.overview'))">
        <Button class="order-overview__mobile-actions-button" @click="handleNewOrder">
          {{ $t(tKey('button.new')) }}
        </Button>
      </div>
    </div>

    <BlockContainer
      class="d-none d-md-block"
      variant="full"
      :title="$t(tKey('subTitle.overview'))"
      ref="orderOverview"
      role="region"
    >
      <div class="d-none d-md-flex">
        <h3 class="normalText sr-only">{{ $t(tKey('label.table')) }}</h3>
        <Table
          small
          :items="recentOrders"
          :fields="fieldsWithActions"
          :responsive="true"
          :striped="true"
          :hover="true"
          @sort-changed="onSortChanged"
          @row-clicked="openOrderDetails"
          :tbody-tr-attr="rowIdentifier"
          :actions="tableActions"
          @details="openOrderDetails"
          @confirm-order="openConfirmDialog"
          @cancel-order="openCancelDialog"
          :show-empty="true"
        >
          <template #cell(iqsrOrderNum)="{ item }">
            <StatusMessage
              v-if="showIqsrOrderNumStatus(item)"
              class="order-overview__pickup-status"
              variant="info"
            >
              {{ pickupStatusText(item) }}
            </StatusMessage>
          </template>
          <template #custom-foot>
            <tr>
              <td class="text-left" :colspan="fieldsWithActions.length">&nbsp;</td>
            </tr>
          </template>
        </Table>
      </div>

      <template #actions class="d-md-none">
        <Button @click="handleNewOrder">{{ $t(tKey('button.new')) }}</Button>
      </template>
    </BlockContainer>

    <Modal
      v-if="showOrderDetailsModal && !showConfirmDialog && !showCancelDialog"
      :value="true"
      :title="$t(tKey('subTitle.orderDetails'))"
      ref="orderDetailsModal"
      @close="showOrderDetailsModal = false"
      @input="showOrderDetailsModal = $event"
    >
      <OrderDetails
        v-if="currentOrderSelected && currentOrderSelected.guid"
        :guid="currentOrderSelected.guid"
        :shopId="currentOrderSelected.shopId"
      />

      <template #footer v-if="modalConfirmVisible || modalCancelVisible">
        <Button
          v-if="modalConfirmVisible"
          :disabled="isLoading"
          icon-left="circle-check"
          @click.stop="openConfirmDialog()"
        >
          {{ $t(tKey('button.confirm')) }}
        </Button>
        <Button
          v-if="modalCancelVisible"
          :disabled="isLoading"
          icon-left="ban"
          :danger="true"
          @click.stop="openCancelDialog()"
        >
          {{ $t(tKey('button.cancel')) }}
        </Button>
      </template>
    </Modal>

    <ConfirmDialog
      v-model="showConfirmDialog"
      :title="$t(tKey('subTitle.confirm'))"
      :message="confirmDialogMessage"
      :ok-label="$t('system.button.yes')"
      :cancel-label="$t('system.button.no')"
      :ok-disabled="isLoading"
      :trap-focus="!isLoading"
      :auto-focus="!isLoading"
      :close-on-esc="!isLoading"
      :close-on-overlay-click="!isLoading"
      @confirm="onConfirmOrder(currentOrderSelected)"
    />

    <ConfirmDialog
      v-model="showCancelDialog"
      :title="$t(tKey('subTitle.cancel'))"
      :message="cancelDialogMessage"
      :ok-label="$t('system.button.yes')"
      :cancel-label="$t('system.button.no')"
      :ok-disabled="isLoading"
      :trap-focus="!isLoading"
      :auto-focus="!isLoading"
      :close-on-esc="!isLoading"
      :close-on-overlay-click="!isLoading"
      @confirm="onCancelOrder(currentOrderSelected)"
    />
  </div>
</template>

<script>
import VueLoading from "vue-loading-overlay";
import BlockContainer from "@/components/layout/BlockContainer.vue";
import CardList from "@/components/data-display/CardList.vue";
import CardListItem from "@/components/data-display/CardListItem.vue";
import Utils from "@/utils/utils";
import orderMixin from "@/components/mixins/orderMixin";
import Table from "@/components/data-display/Table.vue";
import Button from "@/components/actions/Button.vue";
import {useShopStore} from "@/stores/shop";
import OrderDetails from "@/components/sub-components/Order/OrderDetails.vue";
import Modal from "@/components/feedback/Modal.vue";
import ConfirmDialog from "@/components/feedback/ConfirmDialog.vue";
import StatusMessage from "@/components/feedback/StatusMessage.vue";
import {Order, OrderStatusId} from "@/constants/constants";
import {isNotEmpty} from "@/utils/displayUtils";

export default {
  name: 'OrderOverviewPage',
  mixins: [orderMixin],
  components: {
    CardList,
    CardListItem,
    Modal,
    ConfirmDialog,
    StatusMessage,
    OrderDetails,
    Button,
    Table,
    BlockContainer,
    VueLoading
  },
  inject: ["setPageMetaTitle"],
  props: {
    i18nPrefix: {
      type: String,
      required: true,
    },
    configKey: {
      type: String,
      required: true,
    },
    retrieveOrderOptions: {
      type: Object,
      default: () => ({}),
    },
    newOrderRouteName: {
      type: String,
      default: 'OrderMenuPlanner',
    },
    confirmDialogMessageArgsBuilder: {
      type: Function,
      default: null,
    },
    cancelDialogMessageArgsBuilder: {
      type: Function,
      default: null,
    },
    beforeNewOrder: {
      type: Function,
      default: null,
    },
  },
  data() {
    return {
      recentOrders: [],
      table: {},
      isLoading: false,
      currentOrderSelected: {},
      showOrderDetailsModal: false,
      showConfirmDialog: false,
      showCancelDialog: false,
      sortBy: null,
      sortDesc: false,
    }
  },
  setup() {
    const shopStore = useShopStore();
    return {
      shopStore
    };
  },
  computed: {
    orderPageConfig() {
      return this.$config?.[this.configKey] || {};
    },
    showIqsrOrderNumColumn() {
      return this.recentOrders.some((item) => this.showIqsrOrderNumStatus(item));
    },
    fieldsWithActions() {
      const fields = [
        { key: 'date', label: this.$t(this.tKey('tableColumn.date')), sortable: true, formatter: (value, key, item) => item.formattedDate },
        { key: 'status', label: this.$t(this.tKey('tableColumn.status')), sortable: true },
        { key: 'totalAmount', label: this.$t(this.tKey('tableColumn.amount')), sortable: true, class: 'text-right', formatter: (value, key, item) => item.formattedTotal ? item.formattedTotal : '' },
        { key: 'actions', label: this.$t(this.tKey('tableColumn.actions')), tdClass: 'text-right' }
      ];

      if (this.showIqsrOrderNumColumn) {
        fields.splice(3, 0, { key: 'iqsrOrderNum', label: this.$t(this.tKey('tableColumn.iqsrOrderNum')), sortable: true });
      }

      return fields;
    },
    tableActions() {
      return [
        {
          icon: ['fas', 'circle-info'],
          tooltip: this.$t(this.tKey('tooltip.details')),
          ariaLabel: (item) => this.buildTableActionAriaLabel(this.$t(this.tKey('tooltip.details')), item),
          event: 'details',
          show: () => true,
          class: 'order-details-button'
        },
        {
          icon: ['fas', 'circle-check'],
          tooltip: this.$t(this.tKey('button.confirm')),
          ariaLabel: (item) => this.buildTableActionAriaLabel(this.$t(this.tKey('button.confirm')), item),
          event: 'confirm-order',
          show: (item) => this.canConfirmInOverview(item),
          disabled: () => this.isLoading,
          class: 'order-confirm-button'
        },
        {
          icon: ['fas', 'ban'],
          tooltip: this.$t(this.tKey('button.cancel')),
          ariaLabel: (item) => this.buildTableActionAriaLabel(this.$t(this.tKey('button.cancel')), item),
          event: 'cancel-order',
          show: (item) => this.canCancelInOverview(item),
          disabled: () => this.isLoading,
          class: 'order-cancel-button'
        }
      ];
    },
    modalConfirmVisible() {
      return this.canConfirmInDetails(this.currentOrderSelected);
    },
    modalCancelVisible() {
      return this.canCancelInDetails(this.currentOrderSelected);
    },
    confirmDialogMessage() {
      return this.translateWithOptionalArgs('label.confirmDialogMsg', this.confirmDialogMessageArgsBuilder);
    },
    cancelDialogMessage() {
      return this.translateWithOptionalArgs('label.cancelDialogMsg', this.cancelDialogMessageArgsBuilder);
    },
  },
  methods: {
    tKey(suffix) {
      return `${this.i18nPrefix}.${suffix}`;
    },
    resolveDialogMessageArgs(builder) {
      if (typeof builder !== 'function') {
        return undefined;
      }

      return builder({
        currentOrderSelected: this.currentOrderSelected,
        table: this.table,
        shopStore: this.shopStore,
      });
    },
    translateWithOptionalArgs(suffix, argsBuilder) {
      const key = this.tKey(suffix);
      const args = this.resolveDialogMessageArgs(argsBuilder);
      return typeof args === 'undefined'
        ? this.$t(key)
        : this.$t(key, args);
    },
    isActionableStatus(statusId) {
      return statusId === OrderStatusId.ORDERED || statusId === OrderStatusId.READY_TO_BE_PREPARED;
    },
    isOverviewConfirmationEnabled() {
      return !!this.orderPageConfig?.overview?.confirmation;
    },
    isOverviewCancellationEnabled() {
      return !!this.orderPageConfig?.overview?.cancellation;
    },
    isDetailsConfirmationEnabled() {
      return !!this.orderPageConfig?.details?.confirmation;
    },
    isDetailsCancellationEnabled() {
      return !!this.orderPageConfig?.details?.cancellation;
    },
    canConfirmInOverview(item) {
      return !!(this.isOverviewConfirmationEnabled() && this.isActionableStatus(item?.statusId));
    },
    canCancelInOverview(item) {
      return !!(this.isOverviewCancellationEnabled() && this.isActionableStatus(item?.statusId));
    },
    canConfirmInDetails(item) {
      return !!(this.isDetailsConfirmationEnabled() && this.isActionableStatus(item?.statusId));
    },
    canCancelInDetails(item) {
      return !!(this.isDetailsCancellationEnabled() && this.isActionableStatus(item?.statusId));
    },
    isTakeawayOrder(item) {
      return item?.orderTypeId === Order.orderTypId.TAKEAWAY;
    },
    showIqsrOrderNumStatus(item) {
      return this.isTakeawayOrder(item) && isNotEmpty(item?.iqsrOrderNum);
    },
    pickupStatusText(item) {
      return this.$t(this.tKey('label.pickupInstruction'), [item?.iqsrOrderNum]);
    },
    mobileFields(item) {
      const fields = [
        { key: 'shopDescription', label: this.$t(this.tKey('tableColumn.shop')), value: item.shopDescription },
        { key: 'date', label: this.$t(this.tKey('tableColumn.date')), value: item.formattedDate },
        { key: 'status', label: this.$t(this.tKey('tableColumn.status')), value: item.status },
        { key: 'total', label: this.$t(this.tKey('tableColumn.amount')), value: item.formattedTotal },
      ];

      return fields;
    },
    mobileItemAriaLabel(item) {
      return this.$t(this.tKey('tooltip.orderFrom'), [item.formattedDate]);
    },
    mobileItemKey(item, index) {
      return item.guid || item.id || index;
    },
    async refreshOrdersForCurrentDate() {
      const currentDate = Utils.getCurrentDate();
      await this.retrieveOrders(currentDate, currentDate, this.shopStore.wondId, this.retrieveOrderOptions);
    },
    async onConfirmOrder(item) {
      this.isLoading = true;
      const success = await this.confirmOrder(item.guid, this.table.id, item.shopId);
      if (success) {
        await this.refreshOrdersForCurrentDate();
      }
      this.isLoading = false;
      this.showOrderDetailsModal = false;
    },
    async onCancelOrder(item) {
      this.isLoading = true;
      const shopStore = { wondId: item.shopId };
      const success = await this.cancelOrder(shopStore, item.guid);
      if (success) {
        await this.refreshOrdersForCurrentDate();
      }
      this.isLoading = false;
      this.showOrderDetailsModal = false;
    },
    openConfirmDialog(item) {
      if (item) {
        this.currentOrderSelected = item;
      }
      this.showConfirmDialog = true;
    },
    openCancelDialog(item) {
      if (item) {
        this.currentOrderSelected = item;
      }
      this.showCancelDialog = true;
    },
    async handleNewOrder() {
      if (this.isLoading) {
        return;
      }

      if (typeof this.beforeNewOrder === 'function') {
        await this.beforeNewOrder();
      }

      const shopCode = this.shopStore?.wondCode;
      if (!shopCode) {
        this.makeToast('danger', this.$t('notification.failedGeneric'));
        return;
      }

      this.isLoading = true;
      const immediateOrderStarted = await this.doImmediateOrder(shopCode);
      this.isLoading = false;
      if (!immediateOrderStarted) {
        return;
      }

      this.$navigate(this.newOrderRouteName);
    },
    openOrderDetails(item) {
      this.currentOrderSelected = item;
      this.showOrderDetailsModal = true;
    },
    onSortChanged({ sortBy, sortDesc }) {
      this.sortBy = sortBy;
      this.sortDesc = sortDesc;
    },
    rowIdentifier(item, type) {
      if (type !== 'row') {
        return {};
      }
      return {
        title: `${this.$t(this.tKey('tooltip.orderFrom'), [item.formattedDate])}`
      }
    },
    buildTableActionAriaLabel(actionLabel, item) {
      return `${actionLabel} - ${this.$t(this.tKey('tooltip.orderFrom'), [item?.formattedDate])}`;
    },
  },
  async created() {
    await this.setPageMetaTitle(this.shopStore.name);
    await this.refreshOrdersForCurrentDate();
  },
}
</script>

<style scoped>
.order-overview__mobile-actions {
  position: sticky;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10;
  padding-bottom: calc(12px + env(safe-area-inset-bottom, 0px));
}

.order-overview__mobile-actions-button {
  width: 100%;
}

.order-overview__pickup-status {
  width: 100%;
}
</style>

